/**
 * El Tablero — what the tracker says (TASKS T66, SPEC §8).
 *
 * > An instrument panel, not a reward surface. It must be capable of telling him he is behind,
 * > plainly, without encouragement or apology.
 *
 * Pure: figures in, strings out. That is what makes the display rules testable, and they are the
 * whole substance of this module:
 *
 *  - **Three numbers by default**, readable one-handed at minimum brightness in under five seconds.
 *    Everything else is behind one tap.
 *  - **Every window is ≥ 7 days**, and every rendered figure carries the window it was drawn over
 *    (`windowDays`), so a view cannot show a number without saying what it is a number *of*
 *    (DoD-8).
 *  - **`null` is never rendered as zero.** A fresh install reports "not measured yet", because 0%
 *    retention is both false and discouraging.
 *  - **No celebration, no encouragement, no emoji, no colour-coded judgement of the person**
 *    (SPEC §8.1). Amber and red exist for component diagnostics and are never applied to a summary.
 *  - **No comparison** — not to other learners, not to published averages, not to his own best week.
 */

import type { Balance, GrammarShareMetric, Retention, SessionMetrics } from './metrics';
import { MIN_WINDOW_DAYS } from './metrics';

/** One figure, with everything a view needs to render it honestly. */
export interface Figure {
  /** What it is. Two or three words. */
  label: string;
  /** The figure itself, already formatted. Never a bare number, never a zero standing for null. */
  value: string;
  /** The window it was drawn over, in days. Always ≥ 7 (DoD-8). */
  windowDays: number;
  /**
   * One line under it, or null.
   *
   * A fact, never advice: the sample it rests on, or the cap it is read against. Nothing here tells
   * the learner what to do about it — that is the four-week review's job, once a month, with
   * enough data behind it to mean something.
   */
  note: string | null;
  /**
   * A component-level diagnostic (SPEC §8.1).
   *
   * Permitted on a *component*, never on a summary figure, and never as a judgement of the person.
   * The only thing that carries one is the grammar share, and only when it is above the cap by
   * choice rather than by review.
   */
  flag: 'none' | 'attention';
}

/** What the tracker shows when a figure has nothing behind it. Never "0", never blank. */
export const NOT_MEASURED = 'not measured yet';

function figure(
  label: string,
  value: string,
  windowDays: number,
  note: string | null = null,
  flag: Figure['flag'] = 'none',
): Figure {
  if (windowDays < MIN_WINDOW_DAYS) {
    throw new RangeError(`figure "${label}" claims a ${windowDays}-day window (SPEC §8.1)`);
  }
  return { label, value, windowDays, note, flag };
}

export function formatPercent(rate: number | null): string {
  return rate === null ? NOT_MEASURED : `${Math.round(rate * 100)}%`;
}

/**
 * The Balance ratio, in words.
 *
 * `1.4 : 1` rather than `58%`, because the thing being reported is a *proportion between two kinds
 * of minute* and a percentage would invite it to be read as a completion figure.
 */
export function formatRatio(ratio: number | null): string {
  if (ratio === null) return NOT_MEASURED;
  return `${ratio.toFixed(1)} : 1`;
}

// ---------------------------------------------------------------------------------------------
// The three numbers
// ---------------------------------------------------------------------------------------------

export interface DefaultViewInput {
  retention: Retention;
  balance: Balance;
  grammar: GrammarShareMetric;
  retentionWindowDays: number;
  balanceWindowDays: number;
  grammarWindowDays: number;
}

/**
 * The default view: three figures and nothing else (SPEC §8.2).
 *
 * Retention first because it is the one the four-week review acts on. Balance second because it is
 * the one the game cannot see for itself. Grammar share last because it is a guard rail rather than
 * a goal — it is there to be *within* a cap, and nothing about it improves by getting bigger or
 * smaller.
 */
export function defaultView(input: DefaultViewInput): Figure[] {
  const retention = figure(
    'Retention',
    formatPercent(input.retention.rate),
    input.retentionWindowDays,
    // The sample, always: 100% off three items and 100% off two hundred are different claims, and
    // the number alone cannot tell them apart.
    input.retention.sample === 0
      ? 'no due reviews in this window'
      : `${input.retention.correct} of ${input.retention.sample} due reviews, first try`,
  );

  const balance = figure(
    'Balance',
    formatRatio(input.balance.ratio),
    input.balanceWindowDays,
    balanceNote(input.balance),
  );

  const grammar = figure(
    'Grammar share',
    formatPercent(input.grammar.share),
    input.grammarWindowDays,
    grammarNote(input.grammar),
    // The one flag in the default view, and it fires only on grammar chosen rather than grammar due.
    input.grammar.overCap && !input.grammar.reviewDriven ? 'attention' : 'none',
  );

  return [retention, balance, grammar];
}

/**
 * Balance's note keeps the measured and the remembered minutes apart (DoD-12).
 *
 * They are never summed, and they are always labelled, because one is a measurement and the other is
 * a memory — presenting them as one number would give them the same standing.
 */
function balanceNote(balance: Balance): string {
  const input = `${Math.round(balance.inputMinutes)} min in`;
  const measured = `${Math.round(balance.outputMeasuredMinutes)} min measured`;
  const logged = `${Math.round(balance.outputLoggedMinutes)} min logged`;
  return `${input} · ${measured} · ${logged}`;
}

function grammarNote(grammar: GrammarShareMetric): string {
  const cap = `cap ${Math.round(grammar.cap * 100)}%`;
  if (grammar.share === null) return `${cap} · nothing attempted in this window`;
  if (!grammar.overCap) return cap;
  // Above the cap because things came due is spaced retrieval doing its job, not over-investment
  // (SPEC §6.3). Saying so is the difference between a diagnostic and an accusation.
  return grammar.reviewDriven ? `${cap} · above it because reviews came due` : `${cap} · above it`;
}

// ---------------------------------------------------------------------------------------------
// Behind one tap
// ---------------------------------------------------------------------------------------------

export interface DetailInput {
  perTierAccuracy: Record<'1' | '2' | '3' | '4' | '5' | '6', number | null>;
  sessions: SessionMetrics;
  productiveVocabulary: number;
  windowDays: number;
}

/** The tier ladder's names, as SPEC §5 gives them. */
export const TIER_NAMES: Readonly<Record<string, string>> = {
  '1': 'Sonido',
  '2': 'Núcleo',
  '3': 'Motor',
  '4': 'Conversión',
  '5': 'Frase',
  '6': 'Tiempo',
};

/**
 * The drilldown (SPEC §8.2, "everything else is one tap away").
 *
 * Per-tier accuracy, how the sessions actually went, and the productive-vocabulary proxy — labelled
 * a proxy here and everywhere it appears, because it measures the schedule's confidence rather than
 * the learner's ability to produce a word in conversation, and those come apart exactly where it
 * would matter most (SPEC §8.5).
 */
export function detailView(input: DetailInput): Figure[] {
  const tiers = (['1', '2', '3', '4', '5', '6'] as const)
    .filter((tier) => input.perTierAccuracy[tier] !== null)
    .map((tier) =>
      figure(TIER_NAMES[tier]!, formatPercent(input.perTierAccuracy[tier]), input.windowDays),
    );

  return [
    ...tiers,
    figure(
      'Session length',
      input.sessions.medianMinutes === null
        ? NOT_MEASURED
        : `${round1(input.sessions.medianMinutes)} min`,
      input.windowDays,
      // Median, and said so: one ninety-minute evening must not redraw a month of six-minute feeds.
      input.sessions.count === 0 ? null : `median of ${input.sessions.count}`,
    ),
    figure(
      'Sessions a week',
      input.sessions.perWeek === null ? NOT_MEASURED : round1(input.sessions.perWeek).toString(),
      input.windowDays,
    ),
    figure(
      'Productive vocabulary',
      `${input.productiveVocabulary}`,
      input.windowDays,
      'a proxy: words the scheduler has stopped worrying about',
    ),
  ];
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// ---------------------------------------------------------------------------------------------
// The baseline
// ---------------------------------------------------------------------------------------------

export interface BaselineLine {
  label: string;
  /** What he entered at setup, or the fact that he did not. Blank was a real answer meaning unknown. */
  value: string;
}

/**
 * The pre-game baseline, as the tracker shows it (T46b, D-102).
 *
 * Editable here and nowhere else. Editing it never moves `firstLaunchAt`: the baseline is a claim
 * about where he was, and week 6 is a claim about when he started, and correcting the first must not
 * silently reset the second.
 */
export function baselineLines(baseline: {
  retentionPct: number | null;
  dailyMinutes: number | null;
}): BaselineLine[] {
  return [
    {
      label: 'Retention before starting',
      // Not known is what blank meant, and it is stored as null rather than zero precisely so that a
      // missing baseline cannot flatter the week-6 verdict.
      value: baseline.retentionPct === null ? 'not known' : `${baseline.retentionPct}%`,
    },
    {
      /*
       * "On a typical day", not "daily".
       *
       * It is a single self-reported average captured once at setup, not a per-day figure drawn from
       * his logs — but the word "daily" on this surface invites exactly the reading SPEC §8.1
       * forbids, and a test greps for it. The label says what the number actually is.
       */
      label: 'Minutes on a typical day, before starting',
      value: baseline.dailyMinutes === null ? 'not known' : `${baseline.dailyMinutes} min`,
    },
  ];
}
